import { Star, MapPin, Clock, BadgeCheck, MessageCircle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { topRatedProviders, type ServiceProvider } from "@/data/providers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { usePresence } from "@/hooks/use-presence";
import { toast } from "sonner";

function ProviderCard({ provider }: { provider: ServiceProvider }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { getProviderStatus } = usePresence();
  
  // Get real-time presence, fallback to static availability
  const presenceData = getProviderStatus(provider.id);
  const liveStatus = presenceData?.status || provider.availability;

  const getRankColor = (rank: string) => {
    switch (rank) {
      case "Master":
        return "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/40";
      case "Professional":
        return "bg-blue-500/10 text-blue-800 dark:text-blue-300 border-blue-500/40";
      case "Skilled":
        return "bg-green-500/10 text-green-800 dark:text-green-300 border-green-500/40";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const handleBookNow = () => {
    navigate(`/provider/${provider.id}?action=book`);
  };

  const handleChat = () => {
    if (!user) {
      toast.error(t.errors.loginToMessage);
      navigate("/auth");
      return;
    }
    navigate(`/chat?provider=${provider.id}`);
  };

  return (
    <div
      className="group relative overflow-hidden rounded-xl bg-card shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-soft cursor-pointer"
      onClick={() => navigate(`/provider/${provider.id}`)}
    >
      {/* Mobile compact layout (Facebook Marketplace style) */}
      <div className="md:hidden">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={provider.coverImage}
            alt={provider.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 to-transparent" />
          {liveStatus === "online" && (
            <span className="absolute right-2 top-2 flex h-2.5 w-2.5 rounded-full bg-success">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success/75 opacity-75" />
            </span>
          )}
        </div>
        <div className="p-2">
          <div className="flex items-start justify-between gap-1">
            <h3 className="line-clamp-1 flex-1 text-sm font-semibold text-card-foreground">
              {provider.name}
            </h3>
            <div className="flex items-center gap-0.5 text-xs text-amber-500">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              <span>{provider.rating}</span>
            </div>
          </div>
          {provider.businessName && (
            <p className="line-clamp-1 text-xs font-medium text-primary">
              {provider.businessName}
            </p>
          )}
          <p className="mt-0.5 text-sm font-bold text-card-foreground">
            ₦{provider.priceRange.min.toLocaleString()}
            <span className="text-[10px] font-normal text-muted-foreground">
              {" / "}
              {provider.pricingModel === "hourly" ? t.common.hour : t.common.job}
            </span>
          </p>
          <p className="line-clamp-1 text-xs text-muted-foreground">
            {provider.location.area}, {provider.location.lga}
          </p>
        </div>
      </div>

      {/* Desktop full layout */}
      <div className="hidden md:block">
        {/* Cover Image */}
        <div className="relative h-28 overflow-hidden">
          <img
            src={provider.coverImage}
            alt={provider.name}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
          
          {/* Availability Badge - Now uses real-time presence */}
          <div className="absolute right-3 top-3">
            {liveStatus === "online" ? (
              <span className="flex items-center gap-1 rounded-full bg-success/90 px-2 py-1 text-xs font-medium text-success-foreground backdrop-blur-sm">
                <span className="h-2 w-2 animate-pulse-soft rounded-full bg-success-foreground" />
                {t.common.online}
              </span>
            ) : liveStatus === "busy" ? (
              <span className="flex items-center gap-1 rounded-full bg-warning/90 px-2 py-1 text-xs font-medium text-warning-foreground backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-warning-foreground" />
                {t.common.busy}
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-muted/90 px-2 py-1 text-xs font-medium text-muted-foreground backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-muted-foreground" />
                {t.common.offline}
              </span>
            )}
          </div>
        </div>

        {/* Avatar (overlapping) */}
        <div className="relative px-4">
          <Avatar className="absolute -top-8 h-16 w-16 border-4 border-card shadow-md">
            <AvatarImage src={provider.avatar} alt={provider.name} />
            <AvatarFallback className="text-lg font-semibold">
              {provider.name.split(" ").map(n => n[0]).join("")}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Content */}
        <div className="px-4 pb-4 pt-10">
          {/* Name & Verification */}
          <div className="mb-2 flex items-start justify-between">
            <div>
              <h3 className="flex items-center gap-1 font-display text-lg font-semibold text-card-foreground">
                {provider.name}
                {provider.verified.id && provider.verified.phone && (
                  <BadgeCheck className="h-4 w-4 text-info" />
                )}
              </h3>
              <p className="text-sm text-muted-foreground">{provider.designation}</p>
            </div>
            <Badge variant="outline" className={getRankColor(provider.rank)}>
              {provider.rank}
            </Badge>
          </div>

          {/* Business Name */}
          {provider.businessName && (
            <p className="mb-2 text-sm font-medium text-primary">
              {provider.businessName}
            </p>
          )}

          {/* Rating & Experience */}
          <div className="mb-3 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span className="font-semibold">{provider.rating}</span>
              <span className="text-muted-foreground">({provider.reviewCount})</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{provider.yearsOfExperience} yrs</span>
            </div>
          </div>

          {/* Location */}
          <div className="mb-3 flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>
              {provider.location.area}, {provider.location.lga}
            </span>
          </div>

          {/* Services */}
          <div className="mb-4 flex flex-wrap gap-1">
            {provider.services.slice(0, 3).map((service) => (
              <span
                key={service}
                className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {service}
              </span>
            ))}
            {provider.services.length > 3 && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                +{provider.services.length - 3}
              </span>
            )}
          </div>

          {/* Price Range */}
          <div className="mb-4 text-sm">
            <span className="text-muted-foreground">{t.common.from} </span>
            <span className="font-semibold text-primary">
              ₦{provider.priceRange.min.toLocaleString()}
            </span>
            <span className="text-muted-foreground">
              {" / "}
              {provider.pricingModel === "hourly" ? t.common.hour : t.common.job}
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button className="flex-1" size="sm" onClick={(e) => { e.stopPropagation(); handleBookNow(); }}>
              {t.services.book}
            </Button>
            <Button variant="outline" size="sm" aria-label={`Chat with ${provider.name}`} onClick={(e) => { e.stopPropagation(); handleChat(); }}>
              <MessageCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FeaturedProviders() {
  const { t } = useLanguage();

  return (
    <section className="bg-muted/50 py-16 md:py-20">
      <div className="container px-4">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <Badge variant="secondary" className="mb-4">
            {t.home.topRated}
          </Badge>
          <h2 className="mb-4 font-display text-3xl font-bold tracking-tight md:text-4xl">
            {t.home.meetTrustedPros}
          </h2>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            {t.home.trustedProsDescription}
          </p>
        </div>

        {/* Provider Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {topRatedProviders.map((provider) => (
            <ProviderCard key={provider.id} provider={provider} />
          ))}
        </div>

      {/* View All Button */}
      <div className="mt-10 text-center">
        <Button variant="outline" size="lg" asChild>
          <Link to="/browse">{t.home.searchAllProfessionals}</Link>
        </Button>
      </div>
      </div>
    </section>
  );
}
